import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';
import './sentinel-integration-card.css';
import { QCardContent, QTypography, QButton, QTextField, QTooltip } from '@kw/quokka-ui';
import { styled } from '@mui/material';
import { httpRequest } from '../../utils/network.service';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { StyledIntegrationCard } from '../shared/StyledIntegrationCard';

interface SentinelIntegrationCardProps {
  sentinelApiKey?: string | null;
  organizationId?: string | null;
}

export const SentinelIntegrationCard = ({ sentinelApiKey, organizationId }: SentinelIntegrationCardProps) => {
  const [apiKey, setApiKey] = useState(sentinelApiKey);
  const [orgId, setOrgId] = useState(organizationId);
  const [showKey, setShowKey] = useState(false);

  const flags: LaunchDarklyFlags['flags'] = useFlags();
  const { enqueueSnackbar } = useSnackbar();

  if (!flags?.sentinelIntegration || !verifyAuthPermission(AuthPermissions.SETTINGS_INTEGRATIONS_MDM)) return null;

  const refreshSentinelApiKey = async () => {
    const { data, error } = await httpRequest({
      path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/sentinel-integration-service/api-key/refresh`,
      method: 'POST',
    });

    if (data) {
      setApiKey(data?.sentinelApiKey);
      setOrgId(data?.organizationId);
      enqueueSnackbar('Successfully generated Sentinel API Key', { variant: 'success' });
    }

    if (error) {
      enqueueSnackbar('Error generating Sentinel API Key', { variant: 'error' });
    }
  };

  return (
    <StyledIntegrationCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold" gutterBottom>
          <img alt="search-icon" src={'/icons/sentinel.png'} className="sentinel-logo" />
          Microsoft Sentinel
        </StyledQTypography>
        <StyledQTypography variant="bodyRegular" gutterBottom>
          Obtain your API key and Org ID below to configure the Q-scout for Microsoft Sentinel integration. Get mobile device and app
          intelligence from your Q-scout organization directly your Sentinel instance in the form of incidents, alerts and workbooks.{' '}
          <a
            href="https://securitystore.microsoft.com/solutions/quokka.azure-sentinel-solution-quokka/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </StyledQTypography>
        {apiKey && orgId ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <StyledQTypography variant="h5Bold">Organization ID</StyledQTypography>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px', flexGrow: 1 }}>
              <StyledQTypography sx={{ margin: 0 }}>{orgId}</StyledQTypography>
              <QTooltip title={`Copy Org ID`}>
                <QButton
                  sx={{ padding: '0px', height: '20px', minWidth: '20px' }}
                  onClick={() => {
                    navigator.clipboard.writeText(orgId || '');
                    enqueueSnackbar('Copied Organization ID to clipboard', { variant: 'success' });
                  }}
                >
                  <img alt="search-icon" src={'/icons/copy.svg'} />
                </QButton>
              </QTooltip>
            </div>

            <StyledQTypography variant="h5Bold">API Key</StyledQTypography>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', flexGrow: 1 }}>
              <QTextField
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                sx={{ width: '100%', flexGrow: 1 }}
                disabled
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <QButton variant="ghost" addonType="append" onClick={() => setShowKey(!showKey)}>
                      {showKey ? <ViewEyeIcon open /> : <ViewEyeIcon />}
                    </QButton>
                  ),
                }}
              />
              <QTooltip title={`Copy API Key`}>
                <QButton
                  sx={{ padding: '0px', height: '20px', minWidth: '20px' }}
                  onClick={() => {
                    navigator.clipboard.writeText(apiKey || '');
                    enqueueSnackbar('Copied Sentinel API Key to clipboard', { variant: 'success' });
                  }}
                >
                  <img alt="search-icon" src={'/icons/copy.svg'} />
                </QButton>
              </QTooltip>
              <QTooltip title={`Refresh API Key`}>
                <QButton
                  sx={{ padding: 0, width: '20px', minWidth: '20px' }}
                  variant="text"
                  onClick={async () => {
                    await refreshSentinelApiKey();
                  }}
                >
                  <img src="icons/sync-now.svg" className="sync-now-icon" />
                </QButton>
              </QTooltip>
            </div>
          </div>
        ) : (
          <QButton
            variant="filled"
            color="primary"
            onClick={async () => {
              await refreshSentinelApiKey();
            }}
          >
            Get Credentials
          </QButton>
        )}
      </QCardContent>
    </StyledIntegrationCard>
  );
};

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;

const ViewEyeIcon = ({ open = false }) => (
  <svg
    id="view-eye-icon"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    width="20px"
    height="20px"
    viewBox="0 0 64 64"
    enableBackground="new 0 0 64 64"
    xmlSpace="preserve"
  >
    <path
      fill="none"
      stroke="#BDBDBD"
      strokeWidth={2}
      strokeMiterlimit={10}
      d="M1,32c0,0,11,15,31,15s31-15,31-15S52,17,32,17 S1,32,1,32z"
    />
    <circle fill="none" stroke="#BDBDBD" strokeWidth={2} strokeMiterlimit={10} cx={32} cy={32} r={7} />
    {!open && <line fill="none" stroke="#BDBDBD" strokeWidth={2} strokeMiterlimit={10} x1={9} y1={55} x2={55} y2={9} />}
  </svg>
);
